import type { DocCache } from '@colplat/core-client';
import { apiFetch } from './api.js';

/**
 * Network-first, last-known-good on failure.
 *
 * Before this, every read went straight to `fetch` and a dropped connection
 * meant the page could not open a record at all — the list stayed on screen
 * because it was already in memory, but clicking a row failed with "Could not
 * load this transaction", which is a poor answer when the data is 200ms old
 * and sitting right there.
 *
 * Rules, in order of how easy each is to get wrong:
 *
 *  - **Only a transport failure falls back.** `fetch` rejecting (offline, DNS,
 *    connection refused) or a 5xx means "we could not ask". A 401/403/404 is
 *    the server answering, and serving a cached copy over it would show
 *    someone data their access was just revoked from, or a record that has
 *    been deleted.
 *  - **Only a successful response is written.** An error body must never
 *    replace good cached data.
 *  - The result always says whether it came from the network or the cache,
 *    with the age — callers surface that rather than presenting stale data as
 *    live (SPEC's rule that state is never implied by silence).
 *  - **Ask the browser before asking the network.** `navigator.onLine ===
 *    false` means the OS has no interface that could reach anything, so the
 *    `fetch` is certain to fail — and failing takes a connection timeout,
 *    per request, while the user watches. Three parallel reads on page load
 *    turn that into a visibly hung page for no information at all. The
 *    reverse is not true: `onLine === true` proves only that an interface
 *    exists (a captive portal, a dropped VPN, a server that is down all
 *    report `true`), so it is never treated as evidence a request will
 *    succeed — we still try, and still fall back.
 */

export interface ReadResult<T> {
  data: T;
  /** True when the network could not be reached and this came from the cache. */
  stale: boolean;
  /** ISO-8601, present when `stale` — when this copy was last known good. */
  cachedAt?: string;
}

export type ReadOutcome<T> =
  | ({ ok: true } & ReadResult<T>)
  /** The server answered, and it was an error — do not fall back on this. */
  | { ok: false; status: number }
  /** Unreachable, and nothing cached for this key either. */
  | { ok: false; status: 0 };

/** Definitively no network, per the browser. `true` from `navigator.onLine` means nothing — see above. */
export function knownOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export async function readCached<T>(
  cache: DocCache, key: string, path: string, token: string,
): Promise<ReadOutcome<T>> {
  if (knownOffline()) {
    const offlineCopy = cache.get<T>(key);
    return offlineCopy
      ? { ok: true, data: offlineCopy.value, stale: true, cachedAt: offlineCopy.cachedAt }
      : { ok: false, status: 0 };
  }

  try {
    const res = await apiFetch(path, { token });
    if (res.ok) {
      const data = (await res.json()) as T;
      await cache.set(key, data);
      return { ok: true, data, stale: false };
    }
    // The server answered. Take it at its word — see the doc comment.
    if (res.status < 500) return { ok: false, status: res.status };
  } catch {
    // Transport failure; fall through to the cache.
  }

  const cached = cache.get<T>(key);
  if (!cached) return { ok: false, status: 0 };
  return { ok: true, data: cached.value, stale: true, cachedAt: cached.cachedAt };
}

/** "just now" / "12 minutes ago" — for saying how old a cached view is. */
export function ageLabel(iso: string | undefined): string {
  if (!iso) return 'earlier';
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return 'moments ago';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
